import { useState } from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { CAT_LABELS } from "./scoreTone";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

const SEV = { high: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-800", low: "bg-zinc-100 text-zinc-700", info: "bg-sky-50 text-sky-700" };

const Finding = ({ f }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b last:border-0">
      <button type="button" onClick={() => setOpen(!open)} className="w-full py-3 flex items-start gap-3 text-left" data-testid={`pa-finding-${f.id}`}>
        {open ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0" /> : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{f.title}</div>
          {f.what && <div className="text-xs text-muted-foreground">{f.what}</div>}
        </div>
        <Badge variant="outline" className="text-[10px] shrink-0">{CAT_LABELS[f.cat] || f.cat}</Badge>
        <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${SEV[f.severity] || SEV.low}`}>{f.cost ? `-${f.cost} pts` : f.severity}</span>
      </button>
      {open && (
        <div className="pl-7 pb-3 text-sm space-y-1.5">
          {f.why && <p><b>Why it matters:</b> {f.why}</p>}
          {f.fix && <p><b>How to fix:</b> {f.fix}</p>}
          {f.evidence && <p className="text-xs text-muted-foreground"><b>Evidence:</b> {f.evidence}</p>}
        </div>
      )}
    </div>
  );
};

export const FindingsCard = ({ grader }) => {
  const list = [...(grader?.findings || [])].filter((f) => !f.info).sort((a, b) => (b.cost || 0) - (a.cost || 0));
  return (
    <Card className="p-5" data-testid="pa-findings">
      <h2 className="font-semibold flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4 text-amber-600" />What to fix</h2>
      <p className="text-xs text-muted-foreground mb-2">{list.length} problems found · {grader?.passed ?? 0} checks passed. Sorted by points lost.</p>
      {list.length === 0 ? <p className="text-sm text-muted-foreground py-4">No problems found.</p> : list.map((f) => <Finding key={f.id} f={f} />)}
    </Card>
  );
};

export const DesignCard = ({ design, error, pending }) => (
  <Card className="p-5" data-testid="pa-design">
    <h2 className="font-semibold mb-2">Design &amp; headline</h2>
    {error ? <p className="text-sm text-amber-700">{error}</p> : !design ? <p className="text-sm text-muted-foreground">{pending ? "Looking at the site…" : "Not available for this report."}</p> : (
      <div className="space-y-3 text-sm">
        <p><b>{design.design_score}/10:</b> {design.verdict}</p>
        {design.problems?.length > 0 && <ul className="list-disc pl-5 text-muted-foreground space-y-0.5">{design.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>}
        {design.headline_rewrite && (
          <div className="rounded-lg border bg-secondary/50 p-3 space-y-1">
            {design.headline_current && <p className="text-xs text-muted-foreground line-through">{design.headline_current}</p>}
            <p className="font-semibold" data-testid="pa-headline-rewrite">{design.headline_rewrite}</p>
            <p className="text-[11px] text-muted-foreground">Suggested headline</p>
          </div>
        )}
      </div>
    )}
  </Card>
);

export const SeoAeoCard = ({ seo, error }) => (
  <Card className="p-5" data-testid="pa-seo-aeo">
    <h2 className="font-semibold mb-3">SEO &amp; AEO review</h2>
    {error ? <p className="text-sm text-amber-700">{error}</p> : !seo ? <p className="text-sm text-muted-foreground">Reading the site…</p> : (
      <div className="space-y-4 text-sm">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><b>SEO ({seo.seo_grade}):</b> {seo.seo_summary}</div>
          <div><b>AEO ({seo.aeo_grade}):</b> {seo.aeo_summary}</div>
        </div>
        {[["Strengths", seo.strengths], ["Opportunities", seo.opportunities], ["Quick wins", seo.quick_wins]].map(([t, items]) => items?.length > 0 && (
          <div key={t}><div className="font-medium mb-1">{t}</div>
            <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">{items.map((x, i) => <li key={i}>{typeof x === "string" ? x : x.title || x.text || JSON.stringify(x)}</li>)}</ul>
          </div>
        ))}
        {seo.recommendation && <p className="rounded-lg bg-secondary p-3"><b>Recommendation:</b> {seo.recommendation}</p>}
      </div>
    )}
  </Card>
);
